// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
// COMPONENTS
import MUIDataTable from 'mui-datatables';
import { NavLink } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import { Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
// CUSTOM COMPONENTS
import * as alert from '../../../redux/alertSnackBarRedux';
import * as confirmModal from '../../../redux/confirmModalRedux';
// SERVICES AND HELPERS
import * as credentialService from '../../../services/training/credentialService';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';

const CredentialsListingPage = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal, setProcessing } = props;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadTableData();
  }, [showArchived]);

  const loadTableData = () => {
    credentialService
      .getCredentials(showArchived)
      .then((res) => {
        setData(res);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally(() => setLoading(false));
  };

  const archiveCredential = (credentialId) => {
    setProcessing();
    credentialService
      .archiveCredential(credentialId)
      .then((res) => {
        loadTableData();
        showAlert({ message: res.message, variant: 'success' });
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally(() => {
        hideConfirmModal();
      });
  };

  const unarchiveCredential = (credentialId) => {
    setProcessing();
    credentialService
      .unarchiveCredential(credentialId)
      .then((res) => {
        showAlert({ message: res.message, variant: 'success' });
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally(() => {
        hideConfirmModal();
      });
  };

  const columns = [
    {
      name: 'id',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        width: 1,
        print: false,
        download: false,
        setCellProps: () => ({
          'data-aes-field': 'actions',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'actions',
        }),
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className='aes-admin-listing-actions'>
              <NavLink
                to={'/training/credentials/edit-credential/' + data[dataIndex].id}
                className='btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5'
              >
                Edit
              </NavLink>{' '}
              {showArchived ? (
                <button
                  onClick={() =>
                    showConfirmModal({
                      title: 'Unarchive Credential',
                      text:
                        'Are you sure you want to un-archive this credential? <strong>Unarchiving the credential will not unarchive credentials assigned to the users.</strong>',
                      rightBtnText: 'Confirm',
                      btnAction: () => unarchiveCredential(data[dataIndex].id),
                    })
                  }
                  className='btn btn-sm btn-warning font-weight-bold py-2 px-3 px-xxl-5 my-1'
                >
                  Unarchive
                </button>
              ) : (
                <button
                  onClick={() =>
                    showConfirmModal({
                      title: 'Archive Credential',
                      text:
                        'Are you sure you want to archive this credential? <strong>Archiving this credential will set all the credentials assigned to users as archived.</strong>',
                      rightBtnText: 'Confirm',
                      btnAction: () => archiveCredential(data[dataIndex].id),
                    })
                  }
                  className='btn btn-sm btn-warning font-weight-bold py-2 px-3 px-xxl-5 my-1'
                >
                  Archive
                </button>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'trainingType',
      filterType: 'multiselect',
      label: 'Credential Type',
      options: {
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'secondary-one',
          'data-aes-label': 'Credential Type',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'secondary-one',
        }),
      },
    },
    {
      name: 'trainingSubType',
      filterType: 'multiselect',
      label: 'Credential Sub Type',
      options: {
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'secondary-two',
          'data-aes-label': 'Credential Sub Type',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'secondary-two',
        }),
      },
    },
    {
      name: 'name',
      label: 'Credential Name',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'primary',
          'data-aes-label': 'Credential Name',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'primary',
        }),
      },
    },
    {
      name: 'frequency',
      label: 'Frequency',
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'detail-one',
          'data-aes-label': 'Frequency',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'detail-one',
        }),
      },
    },
    {
      name: 'intervalType',
      label: 'Interval Type',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'detail-two',
          'data-aes-label': 'Interval Type',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'detail-two',
        }),
      },
    },
  ];

  return (
    <div className='row aes-admin-listing-page aes-admin-listing-page--credentials'>
      <div className='col-lg-12 col-xxl-12'>
        <div className={`card card-custom card-stretch gutter-b}`}>
          <div className='card-header border-0 py-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label font-weight-bolder text-dark archive-unarchive-span'>
                Credentials
                <OverlayTrigger
                  key={'right'}
                  placement={'right'}
                  overlay={
                    <Tooltip id={`tooltip-right`}>
                      Switch to <strong> {showArchived ? 'active' : 'archived'}</strong>
                    </Tooltip>
                  }
                >
                  <span
                    onClick={() => setShowArchived(!showArchived)}
                    style={{ cursor: 'pointer' }}
                    className={`label label-xl label-inline label-light-${!showArchived ? 'success' : 'warning'}`}
                  >
                    {!showArchived ? 'Active ' : 'Archived'}
                  </span>
                </OverlayTrigger>
              </span>
              <span className='text-muted mt-3 font-weight-bold fz-xs'>View and Manage Credentials</span>
            </h3>
            <div className='card-toolbar'>
              <NavLink to={'/training/credentials/edit-credential/' + 0} className='btn btn-success font-weight-bolder font-size-sm mr-3'>
                <span className='svg-icon svg-icon-lg svg-icon-white'>
                  <SVG src={toAbsoluteUrl('/media/svg/icons/Code/Plus.svg')} />
                </span>{' '}
                Add Credential
              </NavLink>
            </div>
          </div>
          <div className='container-fluid aes-admin-listing p-0'>
            <MUIDataTable
              className='aes-admin-listing-surface'
              data={data}
              columns={columns}
              options={{
                responsive: 'standard',
                rowsPerPage: 25,
                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                selectableRows: 'none',
                setTableProps: () => ({
                  className: 'aes-admin-listing-table',
                }),
                downloadOptions: {
                  filename: 'Credentials.csv',
                  filterOptions: {
                    useDisplayedColumnsOnly: true,
                    useDisplayedRowsOnly: true,
                  },
                },
                textLabels: {
                  body: {
                    noMatch: loading ? <Spinner animation='border' size='lg' variant='info' /> : 'Sorry, no matching records found.',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, { ...alert.actions, ...confirmModal.actions })(CredentialsListingPage);
